import React, { useState, useEffect, useMemo } from 'react';
import { CONSUMABLE_WAREHOUSES as DEFAULT_CONSUMABLE_WAREHOUSES } from '../lib/constants';
import { createMainToSubTransfer, approveAndDispenseFromSub } from '../lib/consumableTransferHelper';

// Helper icons & colors for the 4 consumable warehouses
const WAREHOUSE_CONFIG = {
  'คลังใหญ่ (คลังพัสดุ)': { icon: '🏢', color: '#3b82f6', bg: 'rgba(59, 130, 246, 0.15)', border: 'rgba(59, 130, 246, 0.4)' },
  'คลังย่อย (คลังห้องฟัน)': { icon: '🏥', color: '#06b6d4', bg: 'rgba(6, 182, 212, 0.15)', border: 'rgba(6, 182, 212, 0.4)' },
  'ห้องเก็บของ': { icon: '📦', color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.15)', border: 'rgba(245, 158, 11, 0.4)' },
  'ห้องคลีนิก': { icon: '🩺', color: '#10b981', bg: 'rgba(16, 185, 129, 0.15)', border: 'rgba(16, 185, 129, 0.4)' }
};

const getWarehouseMeta = (name) => {
  return WAREHOUSE_CONFIG[name] || {
    icon: '🏬',
    color: '#8b5cf6',
    bg: 'rgba(139, 92, 246, 0.15)',
    border: 'rgba(139, 92, 246, 0.4)'
  };
};

export const ConsumableTransferModal = ({
  isOpen = false,
  onClose,
  itemMaster = [],
  supplies = [],
  initialItem = null,
  supabase,
  user,
  fetchData,
  CONSUMABLE_WAREHOUSES = DEFAULT_CONSUMABLE_WAREHOUSES
}) => {
  // Safe warehouses list
  const warehouses = useMemo(() => {
    return Array.isArray(CONSUMABLE_WAREHOUSES) && CONSUMABLE_WAREHOUSES.length > 0
      ? CONSUMABLE_WAREHOUSES
      : DEFAULT_CONSUMABLE_WAREHOUSES;
  }, [CONSUMABLE_WAREHOUSES]);

  // Master items in consumable category
  const consumableMasterItems = useMemo(() => {
    return (itemMaster || []).filter(
      item => (item.category || '').trim() === 'วัสดุสิ้นเปลือง' && !item.is_set
    );
  }, [itemMaster]);

  // Unique item names for dropdown/autocomplete
  const uniqueItemNames = useMemo(() => {
    const names = [];
    consumableMasterItems.forEach(item => {
      if (item.item_name && !names.includes(item.item_name)) {
        names.push(item.item_name);
      }
    });
    return names.sort((a, b) => a.localeCompare(b, 'th'));
  }, [consumableMasterItems]);

  // Form states
  const [selectedItemName, setSelectedItemName] = useState('');
  const [selectedMasterId, setSelectedMasterId] = useState(null);
  const [selectedLotNumber, setSelectedLotNumber] = useState('');
  const [sourceWarehouse, setSourceWarehouse] = useState(warehouses[0] || 'คลังใหญ่ (คลังพัสดุ)');
  const [destinationWarehouse, setDestinationWarehouse] = useState(warehouses[1] || 'คลังย่อย (คลังห้องฟัน)');
  const [quantity, setQuantity] = useState(1);
  const [note, setNote] = useState('');
  const [caretakerApprovalName, setCaretakerApprovalName] = useState('');
  const [isCaretakerConfirmed, setIsCaretakerConfirmed] = useState(false);

  // Search & UI interaction states
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  // Workflow condition helpers
  const isMainToSub = useMemo(() => {
    return (sourceWarehouse || '').includes('คลังใหญ่') && (destinationWarehouse || '').includes('คลังย่อย');
  }, [sourceWarehouse, destinationWarehouse]);

  const isSubToStorageOrClinic = useMemo(() => {
    if (!(sourceWarehouse || '').includes('คลังย่อย')) return false;
    return (
      (destinationWarehouse || '').includes('ห้องเก็บของ') ||
      (destinationWarehouse || '').includes('คลีนิก') ||
      (destinationWarehouse || '').includes('sterile') ||
      (destinationWarehouse || '').includes('ห้อง')
    );
  }, [sourceWarehouse, destinationWarehouse]);

  // Synchronize initial item & reset state when modal opens
  useEffect(() => {
    if (!isOpen) {
      setErrorMsg(null);
      setIsSearchOpen(false);
      return;
    }

    const defaultSource = warehouses[0] || 'คลังใหญ่ (คลังพัสดุ)';
    const defaultDest = warehouses[1] || 'คลังย่อย (คลังห้องฟัน)';

    if (initialItem) {
      let matchedMaster = null;

      if (initialItem.masterId) {
        matchedMaster = (itemMaster || []).find(m => String(m.id) === String(initialItem.masterId));
      }

      if (!matchedMaster && initialItem.itemName) {
        const matchingByName = (itemMaster || []).filter(
          m => m.item_name === initialItem.itemName && (m.category || '').trim() === 'วัสดุสิ้นเปลือง'
        );
        if (matchingByName.length > 0) {
          if (initialItem.lotNumber) {
            matchedMaster = matchingByName.find(m => (m.lot_number || '') === initialItem.lotNumber) || matchingByName[0];
          } else {
            matchedMaster = matchingByName[0];
          }
        }
      }

      if (matchedMaster) {
        setSelectedItemName(matchedMaster.item_name || '');
        setSelectedMasterId(matchedMaster.id);
        setSelectedLotNumber(matchedMaster.lot_number || '');
        setSearchQuery(matchedMaster.item_name || '');
      } else {
        const fallbackName = initialItem.itemName || '';
        setSelectedItemName(fallbackName);
        setSearchQuery(fallbackName);
        setSelectedMasterId(initialItem.masterId || null);
        setSelectedLotNumber(initialItem.lotNumber || '');
      }

      const initialSource = initialItem.fromWarehouse && warehouses.includes(initialItem.fromWarehouse)
        ? initialItem.fromWarehouse
        : defaultSource;

      setSourceWarehouse(initialSource);
      const otherWh = warehouses.find(w => w !== initialSource) || defaultDest;
      setDestinationWarehouse(otherWh);
    } else {
      // Default empty state
      setSelectedItemName('');
      setSelectedMasterId(null);
      setSelectedLotNumber('');
      setSearchQuery('');
      setSourceWarehouse(defaultSource);
      setDestinationWarehouse(defaultDest);
    }

    setQuantity(1);
    setNote('');
    setCaretakerApprovalName(user?.name || user?.user_metadata?.name || user?.email || '');
    setIsCaretakerConfirmed(false);
    setErrorMsg(null);
    setIsSearchOpen(false);
  }, [isOpen, initialItem, itemMaster, warehouses, user]);

  // Available lots for the currently selected item name
  const itemLots = useMemo(() => {
    if (!selectedItemName) return [];
    return consumableMasterItems.filter(m => m.item_name === selectedItemName);
  }, [selectedItemName, consumableMasterItems]);

  // Filtered item names based on search query
  const filteredItemNames = useMemo(() => {
    if (!searchQuery.trim()) return uniqueItemNames;
    const q = searchQuery.toLowerCase().trim();
    return uniqueItemNames.filter(name => {
      if (name.toLowerCase().includes(q)) return true;
      const lots = consumableMasterItems.filter(m => m.item_name === name);
      return lots.some(m =>
        (m.barcode && m.barcode.toLowerCase().includes(q)) ||
        (m.lot_number && m.lot_number.toLowerCase().includes(q)) ||
        (m.aliases && m.aliases.toLowerCase().includes(q))
      );
    });
  }, [searchQuery, uniqueItemNames, consumableMasterItems]);

  // Currently active master item object
  const selectedMasterItem = useMemo(() => {
    if (!selectedMasterId) return null;
    return (itemMaster || []).find(m => String(m.id) === String(selectedMasterId)) || null;
  }, [selectedMasterId, itemMaster]);

  const unit = selectedMasterItem?.unit || 'ชิ้น';

  // Stock breakdown across all 4 warehouses for the active master item
  const warehouseStocks = useMemo(() => {
    const stocks = {};
    warehouses.forEach(wh => {
      stocks[wh] = 0;
    });

    if (selectedMasterId) {
      (supplies || []).forEach(s => {
        if (String(s.master_id) === String(selectedMasterId) && warehouses.includes(s.warehouse_name)) {
          stocks[s.warehouse_name] = (stocks[s.warehouse_name] || 0) + (Number(s.item_amount) || 0);
        }
      });
    }

    return stocks;
  }, [selectedMasterId, supplies, warehouses]);

  // Total stock across all 4 warehouses
  const totalStockAcrossWarehouses = useMemo(() => {
    return Object.values(warehouseStocks).reduce((sum, val) => sum + val, 0);
  }, [warehouseStocks]);

  // Available stock in the source warehouse
  const availableAmount = useMemo(() => {
    return warehouseStocks[sourceWarehouse] || 0;
  }, [warehouseStocks, sourceWarehouse]);

  // Handlers for selecting item and lot
  const handleSelectItem = (name) => {
    setSelectedItemName(name);
    setSearchQuery(name);
    setIsSearchOpen(false);

    const lots = consumableMasterItems.filter(m => m.item_name === name);
    if (lots.length > 0) {
      setSelectedMasterId(lots[0].id);
      setSelectedLotNumber(lots[0].lot_number || '');
    } else {
      setSelectedMasterId(null);
      setSelectedLotNumber('');
    }
  };

  const handleSelectLot = (masterId) => {
    const lot = itemLots.find(m => String(m.id) === String(masterId));
    if (lot) {
      setSelectedMasterId(lot.id);
      setSelectedLotNumber(lot.lot_number || '');
    }
  };

  // Warehouse selection handlers with conflict resolution
  const handleSourceChange = (newSource) => {
    setSourceWarehouse(newSource);
    if (destinationWarehouse === newSource) {
      const nextDest = warehouses.find(w => w !== newSource) || '';
      setDestinationWarehouse(nextDest);
    }
  };

  const handleDestinationChange = (newDest) => {
    if (newDest !== sourceWarehouse) {
      setDestinationWarehouse(newDest);
    }
  };

  // Quick increment buttons
  const handleIncrement = (amount) => {
    setQuantity(prev => {
      const current = Number(prev) || 0;
      const next = current + amount;
      return Math.min(availableAmount > 0 ? availableAmount : next, Math.max(1, next));
    });
  };

  const handleSetMax = () => {
    setQuantity(availableAmount > 0 ? availableAmount : 1);
  };

  // Validation logic
  const validationError = useMemo(() => {
    if (!selectedItemName || !selectedMasterId) {
      return 'กรุณาเลือกวัสดุสิ้นเปลืองและล็อตที่ต้องการโอน';
    }
    if (!sourceWarehouse) {
      return 'กรุณาเลือกคลังต้นทาง';
    }
    if (!destinationWarehouse) {
      return 'กรุณาเลือกคลังปลายทาง';
    }
    if (sourceWarehouse === destinationWarehouse) {
      return 'คลังต้นทางและคลังปลายทางต้องไม่เป็นคลังเดียวกัน';
    }
    const numQty = Number(quantity);
    if (!quantity || isNaN(numQty) || numQty <= 0) {
      return 'กรุณาระบุจำนวนที่ต้องการโอน (มากกว่า 0)';
    }
    if (!Number.isInteger(numQty)) {
      return 'จำนวนที่โอนต้องเป็นจำนวนเต็ม';
    }
    if (availableAmount <= 0) {
      return `คลังต้นทาง (${sourceWarehouse}) ไม่มีสต็อกคงเหลือสำหรับโอน (คงเหลือ 0 ${unit})`;
    }
    if (numQty > availableAmount) {
      return `จำนวนที่โอน (${numQty} ${unit}) เกินยอดคงเหลือใน ${sourceWarehouse} (${availableAmount} ${unit})`;
    }
    if (isSubToStorageOrClinic) {
      if (!caretakerApprovalName.trim()) {
        return 'กรุณาระบุชื่อผู้ดูแลคลังย่อยผู้อนุมัติ (Caretaker Approval)';
      }
      if (!isCaretakerConfirmed) {
        return 'กรุณากดยืนยันการอนุมัติการตัดจ่ายวัสดุออกจากคลังย่อย';
      }
    }
    return null;
  }, [
    selectedItemName,
    selectedMasterId,
    sourceWarehouse,
    destinationWarehouse,
    quantity,
    availableAmount,
    unit,
    isSubToStorageOrClinic,
    caretakerApprovalName,
    isCaretakerConfirmed
  ]);

  // Form submission: atomic update/insert on supplies and audit log
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (validationError) return;

    if (!supabase) {
      setErrorMsg('ไม่พบการเชื่อมต่อฐานข้อมูล Supabase');
      return;
    }

    setIsSubmitting(true);
    setErrorMsg(null);

    const numQty = Number(quantity);

    try {
      if (isMainToSub) {
        // Flow 1: Main to Sub Warehouse Transfer (pending receipt at sub-warehouse)
        const transferResult = await createMainToSubTransfer({
          supabase,
          masterItem: selectedMasterItem,
          sourceWarehouse,
          destinationWarehouse,
          quantity: numQty,
          transferredBy: user?.name || user?.user_metadata?.name || user?.email || 'Admin',
          note
        });

        if (typeof fetchData === 'function') {
          try {
            await fetchData();
          } catch (fetchErr) {
            console.warn('fetchData callback error:', fetchErr);
          }
        }

        if (typeof window !== 'undefined' && window.alert) {
          window.alert(
            `✅ บันทึกรายการโอนย้ายสำเร็จ (รหัส: ${transferResult.transferNumber})\n\n` +
            `ระบบตัดยอดสต็อกใน ${sourceWarehouse} เรียบร้อยแล้ว\n` +
            `รายการอยู่ในสถานะ "รอรับเข้า" เพื่อรอให้ผู้ดูแลคลังย่อยกดยืนยันรับของและติดแท็ก QR Code เข้าคลังย่อย`
          );
        }

        if (typeof onClose === 'function') {
          onClose();
        }
        return;
      }

      if (isSubToStorageOrClinic) {
        // Flow 2: Sub Warehouse Outbound Dispense with Caretaker Approval
        await approveAndDispenseFromSub({
          supabase,
          masterItem: selectedMasterItem,
          destinationWarehouse,
          quantity: numQty,
          approvedBy: caretakerApprovalName.trim(),
          note
        });

        if (typeof fetchData === 'function') {
          try {
            await fetchData();
          } catch (fetchErr) {
            console.warn('fetchData callback error:', fetchErr);
          }
        }

        if (typeof window !== 'undefined' && window.alert) {
          window.alert(
            `✅ อนุมัติและตัดจ่ายของออกจากคลังย่อยสำเร็จ!\n\n` +
            `โอนไปยัง ${destinationWarehouse} จำนวน ${numQty} ${unit}\n` +
            `ผู้อนุมัติ: ${caretakerApprovalName.trim()}`
          );
        }

        if (typeof onClose === 'function') {
          onClose();
        }
        return;
      }

      // Flow 3: Standard Immediate Transfer (for all other warehouse movements)
      // 1. Fetch live source supply record from Supabase
      const { data: sourceRows, error: sourceFetchErr } = await supabase
        .from('supplies')
        .select('*')
        .eq('master_id', selectedMasterId)
        .eq('warehouse_name', sourceWarehouse);

      if (sourceFetchErr) throw new Error(`ตรวจสอบสต็อกต้นทางไม่สำเร็จ: ${sourceFetchErr.message}`);

      const sourceSupply = sourceRows && sourceRows.length > 0 ? sourceRows[0] : null;
      const currentSourceAmount = Number(sourceSupply?.item_amount) || 0;

      if (!sourceSupply || currentSourceAmount < numQty) {
        throw new Error(`ยอดคงเหลือในคลังต้นทาง (${sourceWarehouse}) มีเพียง ${currentSourceAmount} ${unit} ไม่เพียงพอสำหรับการโอน ${numQty} ${unit}`);
      }

      // 2. Decrement source warehouse supply
      const newSourceAmount = currentSourceAmount - numQty;
      const { error: sourceUpdateErr } = await supabase
        .from('supplies')
        .update({
          item_amount: newSourceAmount,
          last_updated: new Date().toISOString()
        })
        .eq('id', sourceSupply.id);

      if (sourceUpdateErr) throw new Error(`ไม่สามารถตัดสต็อกต้นทางได้: ${sourceUpdateErr.message}`);

      // 3. Increment or insert destination warehouse supply
      const { data: destRows, error: destFetchErr } = await supabase
        .from('supplies')
        .select('*')
        .eq('master_id', selectedMasterId)
        .eq('warehouse_name', destinationWarehouse);

      if (destFetchErr) throw new Error(`ตรวจสอบสต็อกปลายทางไม่สำเร็จ: ${destFetchErr.message}`);

      const destSupply = destRows && destRows.length > 0 ? destRows[0] : null;

      if (destSupply) {
        const newDestAmount = (Number(destSupply.item_amount) || 0) + numQty;
        const { error: destUpdateErr } = await supabase
          .from('supplies')
          .update({
            item_amount: newDestAmount,
            last_updated: new Date().toISOString()
          })
          .eq('id', destSupply.id);

        if (destUpdateErr) throw new Error(`ไม่สามารถเพิ่มสต็อกปลายทางได้: ${destUpdateErr.message}`);
      } else {
        const { error: destInsertErr } = await supabase
          .from('supplies')
          .insert([{
            master_id: selectedMasterId,
            warehouse_name: destinationWarehouse,
            item_amount: numQty,
            last_updated: new Date().toISOString()
          }]);

        if (destInsertErr) throw new Error(`ไม่สามารถสร้างรายการสต็อกปลายทางได้: ${destInsertErr.message}`);
      }

      // 4. Insert audit log row into logs table
      const lotNumber = selectedMasterItem?.lot_number || selectedLotNumber || '';
      const userIdentifier = user?.name || user?.user_metadata?.name || user?.email || 'Admin';
      const noteSuffix = note && note.trim() ? ` [หมายเหตุ: ${note.trim()}]` : '';
      const actionLog = `โอนย้ายจาก ${sourceWarehouse} ไปยัง ${destinationWarehouse} จำนวน ${numQty} ${unit} (Lot: ${lotNumber || '-'}) โดย ${userIdentifier}${noteSuffix}`;

      const { error: logErr } = await supabase
        .from('logs')
        .insert([{
          item_name: selectedMasterItem?.item_name || selectedItemName || '',
          warehouse: destinationWarehouse,
          action: actionLog
        }]);

      if (logErr) {
        console.warn('บันทึก Log ไม่สำเร็จ:', logErr);
      }

      // 5. Trigger fetchData callback if available
      if (typeof fetchData === 'function') {
        try {
          await fetchData();
        } catch (fetchErr) {
          console.warn('fetchData callback error:', fetchErr);
        }
      }

      // 6. Notify user and close modal
      if (typeof window !== 'undefined' && window.alert) {
        window.alert(`✅ โอนย้ายสำเร็จ!\n\n${actionLog}`);
      }

      if (typeof onClose === 'function') {
        onClose();
      }
    } catch (err) {
      console.error('Consumable transfer error:', err);
      setErrorMsg(err.message || 'เกิดข้อผิดพลาดในการโอนย้ายสต็อก');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="sterile-modal-overlay"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(10, 15, 30, 0.78)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '16px',
        boxSizing: 'border-box'
      }}
      onClick={() => {
        if (!isSubmitting && typeof onClose === 'function') onClose();
      }}
    >
      <div
        className="sterile-modal-content"
        style={{
          background: 'linear-gradient(145deg, #1e1b4b 0%, #0f172a 100%)',
          color: '#f8fafc',
          borderRadius: '20px',
          border: '1px solid rgba(255, 255, 255, 0.14)',
          boxShadow: '0 25px 60px rgba(0, 0, 0, 0.65)',
          maxWidth: '680px',
          width: '100%',
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          animation: 'sterileScaleIn 0.25s cubic-bezier(0.16, 1, 0.3, 1)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'rgba(255, 255, 255, 0.02)'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                width: '42px',
                height: '42px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.3rem',
                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.35)'
              }}
            >
              🔄
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, letterSpacing: '-0.02em', color: '#ffffff' }}>
                โอนย้ายวัสดุสิ้นเปลืองระหว่างคลัง
              </h2>
              <p style={{ margin: '3px 0 0 0', fontSize: '0.84rem', color: '#94a3b8' }}>
                จัดการโอนย้ายสต็อกระหว่างคลังพัสดุ คลังห้องฟัน ห้องเก็บของ และห้องคลีนิก
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            style={{
              background: 'rgba(255, 255, 255, 0.08)',
              border: 'none',
              borderRadius: '10px',
              width: '36px',
              height: '36px',
              color: '#94a3b8',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              fontSize: '1.1rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.15s ease'
            }}
            title="ปิดหน้าต่าง"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Scrollable Form Body */}
        <div style={{ padding: '22px 24px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Error Banner */}
          {errorMsg && (
            <div
              style={{
                background: 'rgba(239, 68, 68, 0.15)',
                border: '1px solid rgba(239, 68, 68, 0.4)',
                borderRadius: '12px',
                padding: '12px 16px',
                color: '#fca5a5',
                fontSize: '0.88rem',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}
            >
              <span style={{ fontSize: '1.2rem' }}>⚠️</span>
              <span>{errorMsg}</span>
            </div>
          )}

          {/* 1. Item Selection (Search / Autocomplete) */}
          <div style={{ position: 'relative' }}>
            <label style={{ display: 'block', fontSize: '0.88rem', fontWeight: 600, color: '#cbd5e1', marginBottom: '8px' }}>
              เลือกวัสดุสิ้นเปลือง <span style={{ color: '#ef4444' }}>*</span>
            </label>

            {selectedItemName ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 16px',
                  background: 'rgba(59, 130, 246, 0.08)',
                  border: '1px solid rgba(59, 130, 246, 0.3)',
                  borderRadius: '12px'
                }}
              >
                <div>
                  <div style={{ fontWeight: 600, color: '#60a5fa', fontSize: '1rem' }}>
                    {selectedItemName}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginTop: '2px' }}>
                    หน่วยนับ: <strong style={{ color: '#e2e8f0' }}>{unit}</strong> | หมวดหมู่: วัสดุสิ้นเปลือง
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedItemName('');
                    setSelectedMasterId(null);
                    setSelectedLotNumber('');
                    setSearchQuery('');
                    setIsSearchOpen(true);
                  }}
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    color: '#e2e8f0',
                    borderRadius: '8px',
                    padding: '6px 12px',
                    fontSize: '0.82rem',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                >
                  ✏️ เปลี่ยนรายการ
                </button>
              </div>
            ) : (
              <div style={{ position: 'relative' }}>
                <div style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                  <input
                    type="text"
                    placeholder="🔍 พิมพ์เพื่อค้นหาชื่อวัสดุ หรือบาร์โค้ด..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setIsSearchOpen(true);
                    }}
                    onFocus={() => setIsSearchOpen(true)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      background: 'rgba(255, 255, 255, 0.06)',
                      border: '1px solid rgba(255, 255, 255, 0.18)',
                      borderRadius: '12px',
                      color: '#ffffff',
                      fontSize: '0.92rem',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery('')}
                      style={{
                        position: 'absolute',
                        right: '12px',
                        background: 'none',
                        border: 'none',
                        color: '#94a3b8',
                        cursor: 'pointer',
                        fontSize: '1rem'
                      }}
                    >
                      ✕
                    </button>
                  )}
                </div>

                {/* Autocomplete Dropdown */}
                {isSearchOpen && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 6px)',
                      left: 0,
                      right: 0,
                      background: '#1e293b',
                      border: '1px solid rgba(255, 255, 255, 0.15)',
                      borderRadius: '12px',
                      maxHeight: '220px',
                      overflowY: 'auto',
                      zIndex: 20,
                      boxShadow: '0 12px 32px rgba(0,0,0,0.5)'
                    }}
                  >
                    {filteredItemNames.length === 0 ? (
                      <div style={{ padding: '14px', color: '#94a3b8', fontSize: '0.88rem', textAlign: 'center' }}>
                        ไม่พบวัสดุสิ้นเปลืองที่ตรงกับคำค้นหา
                      </div>
                    ) : (
                      filteredItemNames.map(name => {
                        const lots = consumableMasterItems.filter(m => m.item_name === name);
                        const firstLot = lots[0] || {};
                        return (
                          <div
                            key={name}
                            onClick={() => handleSelectItem(name)}
                            style={{
                              padding: '10px 16px',
                              cursor: 'pointer',
                              borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              transition: 'background 0.12s ease'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(59, 130, 246, 0.15)'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                          >
                            <span style={{ fontWeight: 500, color: '#f8fafc', fontSize: '0.9rem' }}>{name}</span>
                            <span style={{ fontSize: '0.78rem', color: '#94a3b8' }}>
                              {lots.length > 1 ? `${lots.length} ล็อต` : `หน่วย: ${firstLot.unit || 'ชิ้น'}`}
                            </span>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* 2. Lot Selection (if multiple lots exist or lot details) */}
          {selectedItemName && itemLots.length > 1 && (
            <div
              style={{
                background: 'rgba(255, 255, 255, 0.03)',
                padding: '14px 16px',
                borderRadius: '14px',
                border: '1px solid rgba(255, 255, 255, 0.08)'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <label htmlFor="consumable-lot-select" style={{ fontSize: '0.86rem', fontWeight: 600, color: '#cbd5e1' }}>
                  เลือกล็อต (Lot Number) <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <span style={{ fontSize: '0.75rem', background: 'rgba(59, 130, 246, 0.2)', color: '#93c5fd', padding: '2px 8px', borderRadius: '6px' }}>
                  มี {itemLots.length} ล็อตในระบบ
                </span>
              </div>
              <select
                id="consumable-lot-select"
                value={selectedMasterId || ''}
                onChange={(e) => handleSelectLot(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  background: 'rgba(15, 23, 42, 0.8)',
                  border: '1px solid rgba(255, 255, 255, 0.18)',
                  borderRadius: '10px',
                  color: '#ffffff',
                  fontSize: '0.9rem',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              >
                {itemLots.map(lot => {
                  const lotStock = (supplies || []).filter(s => String(s.master_id) === String(lot.id) && warehouses.includes(s.warehouse_name))
                    .reduce((acc, s) => acc + (Number(s.item_amount) || 0), 0);
                  return (
                    <option key={lot.id} value={lot.id} style={{ background: '#1e293b', color: '#fff' }}>
                      Lot: {lot.lot_number || '(ไม่ระบุ)'} {lot.exp_date ? `| EXP: ${lot.exp_date}` : ''} (รวมสต็อก: {lotStock} {lot.unit || 'ชิ้น'})
                    </option>
                  );
                })}
              </select>
            </div>
          )}

          {/* Lot info if only 1 lot exists */}
          {selectedItemName && itemLots.length === 1 && (
            <div
              style={{
                display: 'flex',
                gap: '12px',
                fontSize: '0.82rem',
                color: '#94a3b8',
                background: 'rgba(255, 255, 255, 0.03)',
                padding: '8px 14px',
                borderRadius: '8px',
                border: '1px solid rgba(255, 255, 255, 0.06)'
              }}
            >
              <span>🏷️ Lot: <strong style={{ color: '#f1f5f9' }}>{selectedMasterItem?.lot_number || '-'}</strong></span>
              {selectedMasterItem?.exp_date && (
                <span>📅 หมดอายุ: <strong style={{ color: '#fbbf24' }}>{selectedMasterItem.exp_date}</strong></span>
              )}
            </div>
          )}

          {/* 3. Stock Breakdown in All 4 Warehouses */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <div style={{ fontSize: '0.88rem', fontWeight: 600, color: '#cbd5e1' }}>
                📊 สต็อกคงเหลือปัจจุบันในแต่ละคลัง
              </div>
              {selectedMasterId && (
                <div style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
                  รวมทั้งสิ้น: <strong style={{ color: '#38bdf8' }}>{totalStockAcrossWarehouses}</strong> {unit}
                </div>
              )}
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
                gap: '10px'
              }}
            >
              {warehouses.map(whName => {
                const meta = getWarehouseMeta(whName);
                const stockAmt = warehouseStocks[whName] || 0;
                const isSource = whName === sourceWarehouse;
                const isDest = whName === destinationWarehouse;

                return (
                  <div
                    key={whName}
                    style={{
                      background: isSource
                        ? 'rgba(59, 130, 246, 0.2)'
                        : isDest
                        ? 'rgba(16, 185, 129, 0.2)'
                        : meta.bg,
                      border: isSource
                        ? '2px solid #3b82f6'
                        : isDest
                        ? '2px solid #10b981'
                        : `1px solid ${meta.border}`,
                      borderRadius: '12px',
                      padding: '12px 10px',
                      textAlign: 'center',
                      position: 'relative',
                      boxShadow: (isSource || isDest) ? '0 4px 14px rgba(0,0,0,0.3)' : 'none',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    {/* Badge indicator */}
                    {isSource && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '-8px',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          background: '#3b82f6',
                          color: '#ffffff',
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          padding: '1px 7px',
                          borderRadius: '10px',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        ต้นทาง (จาก)
                      </div>
                    )}
                    {isDest && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '-8px',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          background: '#10b981',
                          color: '#ffffff',
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          padding: '1px 7px',
                          borderRadius: '10px',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        ปลายทาง (ไป)
                      </div>
                    )}

                    <div style={{ fontSize: '1.2rem', marginTop: (isSource || isDest) ? '4px' : '0' }}>
                      {meta.icon}
                    </div>
                    <div style={{ fontSize: '0.78rem', color: '#e2e8f0', fontWeight: 600, marginTop: '4px', lineHeight: 1.2 }}>
                      {whName}
                    </div>
                    <div style={{ fontSize: '1.15rem', fontWeight: 700, color: stockAmt > 0 ? '#ffffff' : '#64748b', marginTop: '6px' }}>
                      {selectedMasterId ? stockAmt : '-'}
                      <span style={{ fontSize: '0.72rem', fontWeight: 400, color: '#94a3b8', marginLeft: '3px' }}>
                        {selectedMasterId ? unit : ''}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* 4. Warehouse Source & Destination Selectors */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr auto 1fr',
              gap: '12px',
              alignItems: 'center',
              background: 'rgba(255, 255, 255, 0.03)',
              padding: '16px',
              borderRadius: '14px',
              border: '1px solid rgba(255, 255, 255, 0.08)'
            }}
          >
            {/* Source Warehouse */}
            <div>
              <label htmlFor="consumable-source-warehouse" style={{ display: 'block', fontSize: '0.84rem', fontWeight: 600, color: '#93c5fd', marginBottom: '6px' }}>
                คลังต้นทาง (จาก) <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <select
                id="consumable-source-warehouse"
                value={sourceWarehouse}
                onChange={(e) => handleSourceChange(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  background: 'rgba(15, 23, 42, 0.8)',
                  border: '1px solid #3b82f6',
                  borderRadius: '10px',
                  color: '#ffffff',
                  fontSize: '0.88rem',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              >
                {warehouses.map(wh => (
                  <option key={wh} value={wh} style={{ background: '#1e293b', color: '#fff' }}>
                    {getWarehouseMeta(wh).icon} {wh} ({warehouseStocks[wh] || 0} {unit})
                  </option>
                ))}
              </select>
              <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: '5px' }}>
                มีอยู่: <strong style={{ color: availableAmount > 0 ? '#38bdf8' : '#ef4444' }}>{availableAmount}</strong> {unit}
              </div>
            </div>

            {/* Direction Arrow */}
            <div style={{ textAlign: 'center', fontSize: '1.4rem', color: '#60a5fa', marginTop: '14px' }}>
              ➔
            </div>

            {/* Destination Warehouse */}
            <div>
              <label htmlFor="consumable-dest-warehouse" style={{ display: 'block', fontSize: '0.84rem', fontWeight: 600, color: '#6ee7b7', marginBottom: '6px' }}>
                คลังปลายทาง (ไป) <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <select
                id="consumable-dest-warehouse"
                value={destinationWarehouse}
                onChange={(e) => handleDestinationChange(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  background: 'rgba(15, 23, 42, 0.8)',
                  border: '1px solid #10b981',
                  borderRadius: '10px',
                  color: '#ffffff',
                  fontSize: '0.88rem',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              >
                {warehouses.map(wh => {
                  const isSame = wh === sourceWarehouse;
                  return (
                    <option key={wh} value={wh} disabled={isSame} style={{ background: '#1e293b', color: isSame ? '#64748b' : '#fff' }}>
                      {getWarehouseMeta(wh).icon} {wh} {isSame ? '(คลังต้นทาง)' : `(${warehouseStocks[wh] || 0} ${unit})`}
                    </option>
                  );
                })}
              </select>
              <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: '5px' }}>
                มีอยู่: <strong style={{ color: '#a7f3d0' }}>{warehouseStocks[destinationWarehouse] || 0}</strong> {unit}
              </div>
            </div>
          </div>

          {/* Workflow Notice Banner: Main -> Sub Warehouse */}
          {isMainToSub && (
            <div
              style={{
                background: 'rgba(6, 182, 212, 0.12)',
                border: '1px solid rgba(6, 182, 212, 0.35)',
                borderRadius: '12px',
                padding: '12px 16px',
                color: '#67e8f9',
                fontSize: '0.86rem',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
                lineHeight: 1.45
              }}
            >
              <span style={{ fontSize: '1.2rem', marginTop: '-1px' }}>ℹ️</span>
              <div>
                <strong>การตัดของเข้าคลังย่อย:</strong> ระบบจะตัดยอดคลังใหญ่และส่งเป็นรายการ 'รอรับเข้า' โดยผู้ดูแลคลังย่อยจะเป็นผู้กดยืนยันรับของและติดแท็ก QR Code เข้าคลังย่อย
              </div>
            </div>
          )}

          {/* Caretaker Approval Banner & Inputs: Sub -> Storage / Clinic */}
          {isSubToStorageOrClinic && (
            <div
              style={{
                background: 'rgba(245, 158, 11, 0.12)',
                border: '1px solid rgba(245, 158, 11, 0.35)',
                borderRadius: '14px',
                padding: '14px 16px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  color: '#fbbf24',
                  fontSize: '0.88rem',
                  fontWeight: 600
                }}
              >
                <span style={{ fontSize: '1.2rem' }}>🔒</span>
                <span>การตัดของออกจากคลังย่อย: ต้องผ่านการอนุมัติจากผู้ดูแลคลังย่อยเสมอ</span>
              </div>

              <div>
                <label
                  htmlFor="consumable-caretaker-name"
                  style={{ display: 'block', fontSize: '0.84rem', fontWeight: 600, color: '#fef3c7', marginBottom: '6px' }}
                >
                  ชื่อผู้ดูแลคลังย่อยผู้อนุมัติ (Caretaker Approval) <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  id="consumable-caretaker-name"
                  type="text"
                  placeholder="ระบุชื่อ-นามสกุล หรือตำแหน่งผู้ดูแลคลังย่อย"
                  value={caretakerApprovalName}
                  onChange={(e) => setCaretakerApprovalName(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    background: 'rgba(15, 23, 42, 0.8)',
                    border: '1px solid rgba(245, 158, 11, 0.4)',
                    borderRadius: '10px',
                    color: '#ffffff',
                    fontSize: '0.9rem',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  cursor: 'pointer',
                  color: '#fef3c7',
                  fontSize: '0.85rem'
                }}
              >
                <input
                  type="checkbox"
                  checked={isCaretakerConfirmed}
                  onChange={(e) => setIsCaretakerConfirmed(e.target.checked)}
                  style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#f59e0b' }}
                />
                <span>ข้าพเจ้ายืนยันการอนุมัติการตัดจ่ายวัสดุออกจากคลังย่อย</span>
              </label>
            </div>
          )}

          {/* 5. Quantity Input & Quick Increment Buttons */}
          <div
            style={{
              background: 'rgba(255, 255, 255, 0.03)',
              padding: '16px',
              borderRadius: '14px',
              border: '1px solid rgba(255, 255, 255, 0.08)'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <label htmlFor="consumable-quantity" style={{ fontSize: '0.86rem', fontWeight: 600, color: '#cbd5e1' }}>
                จำนวนที่ต้องการโอน ({unit}) <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <span style={{ fontSize: '0.78rem', color: '#94a3b8' }}>
                โอนได้สูงสุด: <strong style={{ color: '#38bdf8' }}>{availableAmount}</strong> {unit}
              </span>
            </div>

            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <input
                id="consumable-quantity"
                type="number"
                min="1"
                max={availableAmount > 0 ? availableAmount : 1}
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  background: 'rgba(15, 23, 42, 0.8)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '10px',
                  color: '#ffffff',
                  fontSize: '1.2rem',
                  fontWeight: 700,
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />

              {/* Quick Increment Buttons */}
              <button
                type="button"
                onClick={() => handleIncrement(1)}
                disabled={availableAmount <= 0}
                style={{
                  background: 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  color: '#ffffff',
                  borderRadius: '8px',
                  padding: '10px 14px',
                  fontSize: '0.88rem',
                  fontWeight: 600,
                  cursor: availableAmount <= 0 ? 'not-allowed' : 'pointer',
                  opacity: availableAmount <= 0 ? 0.5 : 1
                }}
              >
                +1
              </button>
              <button
                type="button"
                onClick={() => handleIncrement(5)}
                disabled={availableAmount <= 0}
                style={{
                  background: 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  color: '#ffffff',
                  borderRadius: '8px',
                  padding: '10px 14px',
                  fontSize: '0.88rem',
                  fontWeight: 600,
                  cursor: availableAmount <= 0 ? 'not-allowed' : 'pointer',
                  opacity: availableAmount <= 0 ? 0.5 : 1
                }}
              >
                +5
              </button>
              <button
                type="button"
                onClick={() => handleIncrement(10)}
                disabled={availableAmount <= 0}
                style={{
                  background: 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  color: '#ffffff',
                  borderRadius: '8px',
                  padding: '10px 14px',
                  fontSize: '0.88rem',
                  fontWeight: 600,
                  cursor: availableAmount <= 0 ? 'not-allowed' : 'pointer',
                  opacity: availableAmount <= 0 ? 0.5 : 1
                }}
              >
                +10
              </button>
              <button
                type="button"
                onClick={handleSetMax}
                disabled={availableAmount <= 0}
                style={{
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  border: 'none',
                  color: '#ffffff',
                  borderRadius: '8px',
                  padding: '10px 14px',
                  fontSize: '0.84rem',
                  fontWeight: 700,
                  cursor: availableAmount <= 0 ? 'not-allowed' : 'pointer',
                  opacity: availableAmount <= 0 ? 0.5 : 1,
                  whiteSpace: 'nowrap'
                }}
              >
                โอนทั้งหมด/Max
              </button>
            </div>

            {/* Transfer Summary Preview */}
            {selectedMasterId && Number(quantity) > 0 && Number(quantity) <= availableAmount && (
              <div
                style={{
                  marginTop: '12px',
                  padding: '10px 12px',
                  background: 'rgba(59, 130, 246, 0.08)',
                  border: '1px dashed rgba(59, 130, 246, 0.3)',
                  borderRadius: '8px',
                  fontSize: '0.82rem',
                  color: '#93c5fd',
                  display: 'flex',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: '8px'
                }}
              >
                <span>
                  📉 คงเหลือใน <strong>{sourceWarehouse}</strong> หลังโอน: {availableAmount - Number(quantity)} {unit}
                </span>
                <span>
                  📈 ยอดใหม่ใน <strong>{destinationWarehouse}</strong> หลังโอน: {(warehouseStocks[destinationWarehouse] || 0) + Number(quantity)} {unit}
                </span>
              </div>
            )}
          </div>

          {/* 6. Optional Note Input */}
          <div>
            <label style={{ display: 'block', fontSize: '0.86rem', fontWeight: 600, color: '#cbd5e1', marginBottom: '6px' }}>
              หมายเหตุ / เหตุผลการโอนย้าย (ไม่บังคับ)
            </label>
            <input
              type="text"
              placeholder="ระบุเหตุผล เช่น เติมสต็อกห้องตรวจ, เบิกใช้ประจำวัน..."
              value={note}
              onChange={(e) => setNote(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 14px',
                background: 'rgba(255, 255, 255, 0.06)',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                borderRadius: '10px',
                color: '#ffffff',
                fontSize: '0.9rem',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
          </div>

          {/* Validation Warning Alert */}
          {validationError && selectedItemName && (
            <div
              style={{
                padding: '10px 14px',
                borderRadius: '10px',
                background: 'rgba(239, 68, 68, 0.12)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                color: '#f87171',
                fontSize: '0.84rem',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <span>ℹ️</span>
              <span>{validationError}</span>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div
          style={{
            padding: '18px 24px',
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
            background: 'rgba(15, 23, 42, 0.4)'
          }}
        >
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            style={{
              padding: '10px 20px',
              borderRadius: '10px',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              background: 'transparent',
              color: '#94a3b8',
              fontSize: '0.9rem',
              fontWeight: 600,
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              transition: 'all 0.15s ease'
            }}
          >
            ยกเลิก
          </button>

          <button
            type="button"
            onClick={handleSubmit}
            disabled={Boolean(validationError) || isSubmitting}
            style={{
              padding: '10px 24px',
              borderRadius: '10px',
              border: 'none',
              background: (Boolean(validationError) || isSubmitting)
                ? 'rgba(255, 255, 255, 0.1)'
                : 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
              color: (Boolean(validationError) || isSubmitting) ? '#64748b' : '#ffffff',
              fontSize: '0.92rem',
              fontWeight: 700,
              cursor: (Boolean(validationError) || isSubmitting) ? 'not-allowed' : 'pointer',
              boxShadow: (Boolean(validationError) || isSubmitting) ? 'none' : '0 4px 14px rgba(37, 99, 235, 0.4)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.15s ease'
            }}
          >
            {isSubmitting ? (
              <>
                <span
                  style={{
                    display: 'inline-block',
                    width: '14px',
                    height: '14px',
                    border: '2px solid rgba(255,255,255,0.3)',
                    borderTopColor: '#ffffff',
                    borderRadius: '50%',
                    animation: 'spin 0.8s linear infinite'
                  }}
                />
                กำลังดำเนินการโอนย้าย...
              </>
            ) : (
              <>
                <span>🔄</span>
                <span>ยืนยันการโอนย้ายสต็อก</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConsumableTransferModal;
